import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEST_DEFINITIONS = {
  Yellow: [
    "Vinyl pool swim 1 length",
    "Tread water for 30 seconds",
    "Underwater / Breath",
  ],
  Red: [
    "In deep end, jump in and swim 2 laps",
    "Tread for 30 seconds",
    "Float on back for 30 seconds",
  ],
  Blue: [
    "One lap back and forth in main pool with life jacket - swim front crawl",
    "With life jacket swim back and forth front crawl",
    "Deep end - tread for 30 seconds, float on back for 30 seconds with life jacket",
  ],
};

export default function ColorTestOverlay({ camper, onClose, onComplete }) {
  const [selectedColor, setSelectedColor] = useState("Yellow");
  const [tests, setTests] = useState([]);
  const [instructorName, setInstructorName] = useState("");
  const [datePassed, setDatePassed] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingResult, setExistingResult] = useState(null);

  // Initialize tests when color changes
  useEffect(() => {
    const definitions = TEST_DEFINITIONS[selectedColor] || [];
    setTests(
      definitions.map((desc) => ({
        description: desc,
        yes_no: "",
        pass_fail: "",
      }))
    );
    // Load existing result for this color
    loadExistingResult(selectedColor);
  }, [selectedColor, camper.id]);

  const loadExistingResult = async (color) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/test-results/${camper.id}/${color}`
      );
      if (response.data) {
        setExistingResult(response.data);
        // Pre-fill form with existing data
        if (response.data.tests) {
          setTests(response.data.tests);
        }
        if (response.data.instructor_name) {
          setInstructorName(response.data.instructor_name);
        }
        if (response.data.date_passed) {
          setDatePassed(new Date(response.data.date_passed));
        }
      } else {
        setExistingResult(null);
      }
    } catch (error) {
      setExistingResult(null);
    }
    setLoading(false);
  };

  const updateTest = (index, field, value) => {
    const newTests = [...tests];
    newTests[index] = { ...newTests[index], [field]: value };
    setTests(newTests);
  };

  const handleConfirm = async () => {
    // Validate
    const allComplete = tests.every((t) => t.yes_no && t.pass_fail);
    if (!allComplete) {
      toast.error("Please complete all test items");
      return;
    }
    if (!instructorName.trim()) {
      toast.error("Please enter instructor name");
      return;
    }

    setSaving(true);
    try {
      const testResult = {
        camper_id: camper.id,
        swim_color: selectedColor,
        tests,
        instructor_name: instructorName.trim(),
        date_passed: format(datePassed, "yyyy-MM-dd"),
      };

      await axios.post(`${API}/test-results`, testResult);
      toast.success(`${selectedColor} test saved!`);

      // Fetch updated camper data
      const camperResponse = await axios.get(`${API}/campers/${camper.id}`);
      onComplete(camperResponse.data);
    } catch (error) {
      toast.error("Failed to save test results");
      console.error(error);
    }
    setSaving(false);
  };

  const getColorClass = (color) => {
    switch (color) {
      case "Yellow":
        return "bg-yellow-500 hover:bg-yellow-600";
      case "Red":
        return "bg-red-500 hover:bg-red-600";
      case "Blue":
        return "bg-cyan-500 hover:bg-cyan-600";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="color-test-overlay">
      {/* Backdrop - Light */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Content - Light Mode */}
      <div className="relative h-full overflow-y-auto">
        <div className="min-h-full flex items-start justify-center p-4 pt-8 pb-20">
          <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-black">
                  Color Test
                </h2>
                <p className="text-sm text-gray-600">
                  {camper.full_name || `${camper.first_name} ${camper.last_name}`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="close-overlay-btn"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Color Selector */}
            <div className="p-4 border-b border-gray-200">
              <Label className="text-black mb-2 block font-medium">Select Swim Color</Label>
              <Select value={selectedColor} onValueChange={setSelectedColor}>
                <SelectTrigger
                  className="w-full h-12 bg-white border-gray-300 text-black"
                  data-testid="color-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  <SelectItem value="Yellow" className="text-black">
                    Yellow
                  </SelectItem>
                  <SelectItem value="Red" className="text-black">
                    Red
                  </SelectItem>
                  <SelectItem value="Blue" className="text-black">
                    Blue
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Show completed status if exists */}
              {existingResult && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <Check className="h-4 w-4" />
                    <span className="font-medium">Completed</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Instructor: <span className="text-black font-medium">{existingResult.instructor_name}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Date: <span className="text-black font-medium">{existingResult.date_passed}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                Loading test results...
              </div>
            ) : (
            <>
            {/* Test Items */}
            <div className="p-4 space-y-4">
              {tests.map((test, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-50 rounded-xl border border-gray-200"
                  data-testid={`test-item-${index}`}
                >
                  <p className="text-black font-medium mb-4">{test.description}</p>
                  
                  <div className="flex gap-4">
                    {/* Yes/No Toggle */}
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-2">Response</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => updateTest(index, "yes_no", "yes")}
                          className={cn(
                            "flex-1 py-2 rounded-lg font-medium transition-colors border",
                            test.yes_no === "yes"
                              ? "bg-green-500 text-white border-green-500"
                              : "bg-white text-black border-gray-300 hover:bg-gray-100"
                          )}
                          data-testid={`test-${index}-yes`}
                        >
                          YES
                        </button>
                        <button
                          onClick={() => updateTest(index, "yes_no", "no")}
                          className={cn(
                            "flex-1 py-2 rounded-lg font-medium transition-colors border",
                            test.yes_no === "no"
                              ? "bg-red-500 text-white border-red-500"
                              : "bg-white text-black border-gray-300 hover:bg-gray-100"
                          )}
                          data-testid={`test-${index}-no`}
                        >
                          NO
                        </button>
                      </div>
                    </div>

                    {/* Pass/Fail Dropdown */}
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-2">Result</p>
                      <Select
                        value={test.pass_fail}
                        onValueChange={(v) => updateTest(index, "pass_fail", v)}
                      >
                        <SelectTrigger className="h-10 bg-white border-gray-300 text-black" data-testid={`test-${index}-result`}>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-300">
                          <SelectItem value="pass" className="text-black">Pass</SelectItem>
                          <SelectItem value="fail" className="text-black">Fail</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Instructor and Date */}
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div>
                <Label htmlFor="instructor" className="text-black mb-2 block font-medium">
                  Instructor Name
                </Label>
                <Input
                  id="instructor"
                  value={instructorName}
                  onChange={(e) => setInstructorName(e.target.value)}
                  placeholder="Enter instructor name"
                  className="h-12 bg-white border-gray-300 text-black placeholder:text-gray-400"
                  data-testid="instructor-input"
                />
              </div>

              <div>
                <Label className="text-black mb-2 block font-medium">Date Passed</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-12 justify-start text-left font-normal bg-white border-gray-300 text-black hover:bg-gray-50",
                        !datePassed && "text-gray-400"
                      )}
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {datePassed ? format(datePassed, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-300">
                    <Calendar
                      mode="single"
                      selected={datePassed}
                      onSelect={setDatePassed}
                      initialFocus
                      className="bg-white"
                      data-testid="date-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Confirm Button */}
            <div className="p-4 border-t border-gray-200">
              <Button
                onClick={handleConfirm}
                disabled={saving}
                className={cn(
                  "w-full h-14 font-bold text-lg text-white",
                  getColorClass(selectedColor)
                )}
                data-testid="confirm-btn"
              >
                {saving ? (
                  "Saving..."
                ) : (
                  "Confirm Test Results"
                )}
              </Button>
            </div>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
